import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { CountdownTimer } from './CountdownTimer';
import { useTheme } from '@/contexts/ThemeContext';

const navItems = [
  { path: '/', label: 'Ana Sayfa', icon: HomeIcon },
  { path: '/takvim', label: 'Etkinlik Takvimi', icon: CalendarIcon },
  { path: '/galeri', label: 'Galeri', icon: PhotoIcon },
  { path: '/duyurular', label: 'Duyurular', icon: MegaphoneIcon },
];

// Helper to apply brightness and saturation like CSS filter
function applyDarkModeFilter(r: number, g: number, b: number): string {
  // Apply brightness(0.3) - multiply by 0.3
  let nr = r * 0.3;
  let ng = g * 0.3;
  let nb = b * 0.3;
  
  // Apply saturate(1.2) - increase saturation by 20%
  const gray = 0.2126 * nr + 0.7152 * ng + 0.0722 * nb;
  nr = gray + (nr - gray) * 1.2;
  ng = gray + (ng - gray) * 1.2;
  nb = gray + (nb - gray) * 1.2;
  
  return `rgb(${Math.round(Math.max(0, Math.min(255, nr)))}, ${Math.round(Math.max(0, Math.min(255, ng)))}, ${Math.round(Math.max(0, Math.min(255, nb)))})`;
}

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [bgColorLight, setBgColorLight] = useState('#ffffff');
  const [bgColorDark, setBgColorDark] = useState('#0a1628');
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Extract edge pixel color from logo and create dark variant
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        // Sample top-left corner pixel
        const pixel = ctx.getImageData(0, 0, 1, 1).data;
        const [r, g, b] = [pixel[0], pixel[1], pixel[2]];
        setBgColorLight(`rgb(${r}, ${g}, ${b})`);
        // Apply same filter as the logo gets in dark mode
        setBgColorDark(applyDarkModeFilter(r, g, b));
      }
    };
    img.src = '/mun_logo.png';
  }, []);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Track page views
  useEffect(() => {
    (async () => {
      try { await api.post('/api/public/track', { path: location.pathname }); } catch {}
    })();
  }, [location.pathname]);

  const toggleSidebar = () => setSidebarOpen((v) => !v);

  return (
    <div className="min-h-screen flex relative bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
      {/* Sidebar Overlay (mobile) */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={`fixed z-50 h-screen backdrop-blur-md border-r border-gray-200/40 dark:border-gray-700/40 flex flex-col transition-all duration-300 ease-out ${
          sidebarOpen ? 'w-72 translate-x-0' : 'w-0 lg:w-20 -translate-x-full lg:translate-x-0'
        }`}
        style={{ backgroundColor: theme === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.92)' }}
      >
        <div className={`flex flex-col h-full ${sidebarOpen ? 'opacity-100' : 'opacity-0 lg:opacity-100'} transition-opacity duration-200`}>
          {/* Logo section */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-700">
            <Link to="/" className="flex items-center gap-3">
              <img src="/mun_logo.png" alt="ESDFL MUN" className="h-12 w-12 rounded-lg shadow-sm flex-shrink-0" />
              <div className={`overflow-hidden transition-all duration-300 ${sidebarOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'}`}>
                <div className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap">ESDFL MUN</div>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 overflow-y-auto overflow-x-hidden">
            <div className="space-y-1">
              {navItems.map((item, idx) => (
                <div key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group ${
                        sidebarOpen ? '' : 'justify-center'
                      } ${
                        isActive
                          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 font-semibold shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }`
                    }
                    title={!sidebarOpen ? item.label : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon className={`w-5 h-5 flex-shrink-0 transition-colors ${isActive ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`} />
                        <span className={`overflow-hidden transition-all duration-300 whitespace-nowrap ${sidebarOpen ? 'w-auto opacity-100' : 'w-0 opacity-0'}`}>
                          {item.label}
                        </span>
                        {isActive && sidebarOpen && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
                        )}
                      </>
                    )}
                  </NavLink>
                  {/* Divider between tabs */}
                  {idx < navItems.length - 1 && (
                    <div className={`my-1 border-b border-gray-100 dark:border-gray-700 transition-all duration-300 ${sidebarOpen ? 'mx-3' : 'mx-2'}`} />
                  )}
                </div>
              ))}
            </div>
          </nav>
        </div>
      </aside>

      {/* Main content */}
      <div 
        className={`flex-1 flex flex-col min-w-0 relative z-10 transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-72' : 'lg:ml-20'
        }`}
        style={{ 
          backgroundColor: theme === 'dark' ? bgColorDark : bgColorLight
        }}
      >
        {/* Background logo with dynamic filter */}
        <div 
          className="absolute inset-0 bg-center bg-no-repeat pointer-events-none transition-all duration-300"
          style={{ 
            backgroundImage: 'url(/mun_logo.png)',
            backgroundSize: '50%',
            filter: theme === 'dark' ? 'brightness(0.3) saturate(1.2)' : 'none',
            opacity: theme === 'dark' ? 0.8 : 1,
          }}
        />
        {/* Header with toggle and countdown */}
        <header className="sticky top-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300 relative">
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSidebar}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:scale-105"
                aria-label={sidebarOpen ? 'Menüyü kapat' : 'Menüyü aç'}
              >
                <div className="relative w-6 h-5 flex flex-col justify-between">
                  <span className={`block h-0.5 bg-gray-600 dark:bg-gray-300 rounded transition-all duration-300 origin-left ${sidebarOpen ? 'rotate-45 w-[22px] translate-y-[1px]' : 'w-6'}`} />
                  <span className={`block h-0.5 bg-gray-600 dark:bg-gray-300 rounded transition-all duration-300 ${sidebarOpen ? 'opacity-0 translate-x-3' : 'w-6'}`} />
                  <span className={`block h-0.5 bg-gray-600 dark:bg-gray-300 rounded transition-all duration-300 origin-left ${sidebarOpen ? '-rotate-45 w-[22px] -translate-y-[1px]' : 'w-6'}`} />
                </div>
              </button>
              <Link to="/" className="flex items-center gap-2 lg:hidden">
                <img src="/mun_logo.png" alt="ESDFL MUN" className="h-8 w-8 rounded" />
                <span className="font-bold text-gray-900 dark:text-white">ESDFL MUN</span>
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Countdown in header */}
              <div className="hidden sm:flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                <CountdownTimer compact />
              </div>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                aria-label={theme === 'dark' ? 'Açık moda geç' : 'Koyu moda geç'}
              >
                {theme === 'dark' ? (
                  <SunIcon className="w-5 h-5 text-yellow-400" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 max-w-7xl w-full mx-auto relative z-10">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors duration-300 relative z-10">
          <div className="px-8 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
            © {new Date().getFullYear()} Edirne Süleyman Demirel Fen Lisesi
          </div>
        </footer>
      </div>
    </div>
  );
}

// Icons
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
    </svg>
  );
}

function PhotoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );
}

function MegaphoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
    </svg>
  );
}
